/**
 * chain-eos/governance.ts
 *
 * Block Producer (BP) data and voting for EOS / Vaulta.
 * Voting action: eosio::voteproducer
 * Max 30 producers per vote. Must be sorted alphabetically.
 */

import { Name } from '@wharfkit/antelope'
import { EOS_RPC_ENDPOINTS } from './config.js'

export interface BlockProducer {
  account: string
  totalVotes: string
  totalVotesNum: number
  rank: number
  isActive: boolean        // rank ≤ 21
  url: string
  location: number
  countryCode: string      // 2-letter ISO from location field
  producerKey: string
}

export interface VoteInfo {
  voter: string
  proxy: string
  producers: string[]
  /** Raw staked integer (÷ 10000 = EOS amount) */
  staked: number
  /** Human-readable staked EOS */
  stakedEos: string
  lastVoteWeight: string
  isProxy: boolean
}

// ─── Raw RPC helper ───────────────────────────────────────────────────────────

async function rpcPost<T>(path: string, body: unknown, endpointIndex = 0): Promise<T> {
  const endpoints = EOS_RPC_ENDPOINTS
  const url = `${endpoints[endpointIndex % endpoints.length]}${path}`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`RPC ${res.status}: ${path}`)
  return res.json() as Promise<T>
}

// ─── Fetch BPs ────────────────────────────────────────────────────────────────

export async function getTopProducers(limit = 100): Promise<BlockProducer[]> {
  type ProducerRow = {
    owner: string
    total_votes: string
    url?: string
    location?: number
    producer_key?: string
  }
  type ProducersResponse = { rows: ProducerRow[] }

  const result = await rpcPost<ProducersResponse>('/v1/chain/get_producers', {
    json: true,
    lower_bound: '',
    limit,
  })

  return result.rows.map((row, index) => ({
    account: String(row.owner),
    totalVotes: formatVotes(row.total_votes),
    totalVotesNum: parseFloat(row.total_votes),
    rank: index + 1,
    isActive: index < 21,
    url: String(row.url ?? '').replace(/\/$/, ''),
    location: Number(row.location ?? 0),
    countryCode: locationToCountry(Number(row.location ?? 0)),
    producerKey: String(row.producer_key ?? ''),
  }))
}

/**
 * Get voter info for an account (reads `eosio.voters` table).
 */
export async function getVoterInfo(accountName: string): Promise<VoteInfo | null> {
  try {
    type TableRow = {
      owner: string
      proxy?: string
      producers?: string[]
      staked?: number
      last_vote_weight?: string
      is_proxy?: boolean
    }
    type TableResponse = { rows: TableRow[] }

    const result = await rpcPost<TableResponse>('/v1/chain/get_table_rows', {
      json: true,
      code: 'eosio',
      scope: 'eosio',
      table: 'voters',
      lower_bound: accountName,
      upper_bound: accountName,
      limit: 1,
    })

    if (!result.rows || result.rows.length === 0) return null
    const row = result.rows[0]!

    const staked = Number(row.staked ?? 0)
    return {
      voter: String(row.owner),
      proxy: String(row.proxy ?? ''),
      producers: Array.isArray(row.producers) ? row.producers.map(String) : [],
      staked,
      stakedEos: formatEos(staked),
      lastVoteWeight: String(row.last_vote_weight ?? '0'),
      isProxy: Boolean(row.is_proxy ?? false),
    }
  } catch {
    return null
  }
}

// ─── Vote ─────────────────────────────────────────────────────────────────────

/**
 * Submit a `voteproducer` transaction for `voter` → `producers` list.
 * BPs are sorted alphabetically (required by the contract).
 * Pass empty array to clear all votes.
 */
export async function voteForProducers(
  session: import('./session.js').Session,
  voter: string,
  producers: string[],
): Promise<string> {
  if (producers.length > 30) throw new Error('Cannot vote for more than 30 producers')
  const sorted = [...producers].sort()

  const result = await session.transact({
    action: {
      account: 'eosio',
      name: 'voteproducer',
      authorization: [{ actor: voter, permission: 'active' }],
      data: {
        voter: Name.from(voter),
        proxy: Name.from(''),
        producers: sorted.map(p => Name.from(p)),
      },
    },
  })

  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatVotes(raw: string | number): string {
  const num = parseFloat(String(raw))
  if (num >= 1e15) return `${(num / 1e15).toFixed(2)}P`
  if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`
  if (num >= 1e9)  return `${(num / 1e9).toFixed(2)}B`
  if (num >= 1e6)  return `${(num / 1e6).toFixed(2)}M`
  return num.toFixed(0)
}

/** staked is in units of 0.0001 EOS (integer) */
function formatEos(staked: number): string {
  return (staked / 10000).toFixed(4)
}

/**
 * EOS stores location as a custom integer. In practice producers enter their
 * ISO 3166-1 numeric code (e.g. 840 = US). We map the most common ones.
 */
function locationToCountry(loc: number): string {
  const map: Record<number, string> = {
    0: '', 840: 'US', 156: 'CN', 840001: 'US',
    826: 'GB', 276: 'DE', 392: 'JP', 410: 'KR',
    36: 'AU', 124: 'CA', 76: 'BR', 643: 'RU',
    356: 'IN', 528: 'NL', 756: 'CH', 702: 'SG',
    344: 'HK', 158: 'TW', 250: 'FR', 380: 'IT',
    724: 'ES', 616: 'PL', 620: 'PT', 352: 'IS',
    554: 'NZ', 710: 'ZA', 784: 'AE', 682: 'SA',
  }
  return map[loc] ?? ''
}

/** Return country flag emoji from 2-letter code */
export function countryFlag(code: string): string {
  if (!code || code.length !== 2) return '🌐'
  return code
    .toUpperCase()
    .split('')
    .map(c => String.fromCodePoint(0x1f1e0 + c.charCodeAt(0) - 65))
    .join('')
}
